// src/components/brand/no-runs-screen.tsx
import { useState } from "react";
import { Radio, Play, RefreshCcw, LogOut, Loader2 } from "lucide-react";
import { UpmodeLogo } from "../login/upmode-logo";

interface NoRunsScreenProps {
  brand: string;
  /** Kick off the first monitoring run for this brand. */
  onRun: () => void;
  /** Re-check run history (an admin/onboarding run may have just landed). */
  onRetry: () => Promise<void> | void;
  onLogout: () => void;
}

/**
 * Shown to a brand-scoped user whose assigned brand has no monitoring
 * runs yet. We do NOT auto-run on login — the user explicitly starts
 * the first analysis from here. Nav-less, so it carries the logo.
 */
export function NoRunsScreen({ brand, onRun, onRetry, onLogout }: NoRunsScreenProps) {
  const [checking, setChecking] = useState(false);

  const handleRetry = async () => {
    setChecking(true);
    try {
      await onRetry();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--background)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* Logo bar — this screen has no left navigation */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--border)",
          backgroundColor: "var(--card)",
        }}
      >
        <UpmodeLogo />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(16px, 4vh, 32px) clamp(16px, 4vw, 28px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%)
            `,
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            width: "100%",
            maxWidth: 520,
            position: "relative",
            zIndex: 1,
            backgroundColor: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "clamp(28px, 4vh, 44px) clamp(24px, 4vw, 40px)",
            boxShadow: "var(--elevation-lg)",
            textAlign: "center",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background:
                "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
              borderRadius: "16px 16px 0 0",
              opacity: 0.8,
            }}
          />

          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              margin: "0 auto 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "var(--alpha-teal-10)",
              border: "1px solid var(--alpha-teal-20)",
            }}
          >
            <Radio style={{ width: 30, height: 30, color: "var(--alpha-teal)" }} />
          </div>

          <h1
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-2xl)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--foreground)",
              margin: 0,
              lineHeight: "var(--leading-tight)",
            }}
          >
            No analysis yet
          </h1>

          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--text-base)",
              color: "var(--muted-foreground)",
              lineHeight: "var(--leading-relaxed)",
              margin: "12px auto 0",
              maxWidth: 400,
            }}
          >
            There's no brand monitoring run for{" "}
            <strong style={{ color: "var(--foreground)" }}>{brand}</strong> yet.
            Run the first analysis to create your AI perception baseline.
          </p>

          <div
            style={{
              marginTop: 26,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <button
              onClick={onRun}
              style={{
                width: "100%",
                height: 48,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                background: "var(--alpha-teal)",
                color: "white",
                border: "none",
                borderRadius: "var(--radius)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Play style={{ width: 18, height: 18 }} />
              Run analysis for {brand}
            </button>

            <button
              onClick={handleRetry}
              disabled={checking}
              style={{
                width: "100%",
                height: 44,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-medium)",
                background: "transparent",
                color: "var(--muted-foreground)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                cursor: checking ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {checking ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
                  Checking…
                </>
              ) : (
                <>
                  <RefreshCcw style={{ width: 16, height: 16 }} />
                  Check for existing runs
                </>
              )}
            </button>

            <button
              onClick={onLogout}
              style={{
                width: "100%",
                height: 40,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                background: "transparent",
                color: "var(--muted-foreground)",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <LogOut style={{ width: 15, height: 15 }} />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}